//RPC socket Wrapper

import { SOCKET_EVENTS, SOCKET_STATE } from "./constants/index";
import { Socket } from "./socket";
import {
  generateJsonRpcRequestPayload,
  getId,
  tryParseJSONObject,
} from "./constants/index";

type Callback = (response: { result?: any; error?: any }) => void;
type Cancel = () => void;

class Request {
  callback: Callback;
  cancel: Cancel;
  timeout: NodeJS.Timeout;
  constructor(callback: Callback, cancel: Cancel) {
    this.callback = callback;
    this.cancel = cancel;
    this.timeout = setTimeout(() => {
      this.callback({ error: "Request Timeout" });
      this.cancel();
    }, 30000);
  }

  handler(result: any, error: any) {
    clearTimeout(this.timeout);
    this.cancel();
    this.callback({ result, error });
  }
}

interface RpcSocketParams {
  id: string;
  url: string;
  stateCB: (status: (typeof SOCKET_STATE)[keyof typeof SOCKET_STATE]) => void;
  messageCB: (message: { result?: any; method?: string; error?: any }) => void;
}

export class RpcSocket {
  private requests: { [id: number]: Request } = {};
  private closed = true;
  private status: (typeof SOCKET_STATE)[keyof typeof SOCKET_STATE] =
    SOCKET_STATE.CLOSED;
  private socket!: Socket;
  private id: string;
  private url: string;
  private stateCB: RpcSocketParams["stateCB"];
  private messageCB: RpcSocketParams["messageCB"];

  constructor({ id, url, stateCB, messageCB }: RpcSocketParams) {
    this.id = id;
    this.url = url;
    this.stateCB = stateCB;
    this.messageCB = messageCB;
    this.connectSocket(false);
  }

  sendRequest(
    { method, params }: { method: string; params: any },
    onResponse: any
  ) {
    // A monotonic id, not a random one in a small range: concurrent requests
    // (e.g. bulk upload registering several files at once) previously used
    // getRandomInt(100, 1000), which collided often enough that a later
    // request's id would overwrite an earlier still-pending one in
    // this.requests, silently losing the earlier request's response (or
    // handing it the wrong response) once the reply came back.
    const id = getId();
    this.socket.sendJson(generateJsonRpcRequestPayload({ id, method, params }));
    this.requests[id] = new Request(onResponse, () => {
      delete this.requests[id];
    });
  }

  connectSocket = (force = false) => {
    if (
      !force &&
      (this.status === SOCKET_STATE.OPEN ||
        this.status === SOCKET_STATE.CONNECTING)
    ) {
      return;
    }
    this.closed = false;
    console.log(`SOCKET CONNECTING ${this.id.toUpperCase()}`);
    this.updateState(SOCKET_STATE.CONNECTING);
    this.socket = new Socket(this.url, true);
    this.socket.on(SOCKET_EVENTS.MESSAGE, this.onSocketMessage.bind(null));
    this.socket.on(SOCKET_EVENTS.CLOSE, this.onSocketClose.bind(null));
    this.socket.on(SOCKET_EVENTS.OPEN, this.onSocketOpen.bind(null));
    this.socket.on(SOCKET_EVENTS.ERROR, () => console.error("Socket Error"));
  };

  onSocketClose = () => {
    console.log(`SOCKET CLOSED ${this.id.toUpperCase()}`);
    if (this.closed) {
      return;
    }
    this.updateState(SOCKET_STATE.CLOSED);
    this.socket.disconnect();
    setTimeout(() => {
      console.log(`SOCKET RECONNECTING ${this.id.toUpperCase()}`);
      this.connectSocket();
    }, 3000);
  };

  onSocketOpen = () => {
    this.updateState(SOCKET_STATE.OPEN);
  };

  closeSocket = () => {
    this.closed = true;
    this.updateState(SOCKET_STATE.CLOSED);
    this.socket.disconnect();
  };

  updateState = (status: any) => {
    this.status = status;
    this.stateCB(status);
  };

  //socket message handlers
  onSocketMessage = (data: any) => {
    const res = tryParseJSONObject(data);
    //console.log(":RPC SOCKET: onSocketMessage:RPC SOCKET:  ", res);
    if (!res) return;
    const request = this.requests[res["id"]];
    if (request) {
      request.handler(res["result"], res["error"]);
    } else {
      this.messageCB({
        result: res["result"],
        method: res["method"],
        error: res["error"],
      });
    }
  };
}
